import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, Button } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { candidateService } from '../services'
import { appName } from '../appConfig'
import { MILESTONE_TYPES, MILESTONE_PILL_VALUES, CLOSED_MILESTONES } from '../helpers/milestones'
import { useCandidateDialog } from '../hooks'

// Groups every bulk action behind one button instead of a row of them.
// "Redigera" (full edit) stays outside this menu — it only ever targets one
// candidate (see editCandidate below) and needs its own `action:"edit"` entry
// for the per-row pencil icon anyway.
const BulkActionsMenu = ({ items, onChangeStage, onReassignOwner, onUpdateSource, onExport, onDelete }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef()

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const run = (fn) => { setOpen(false); fn(items) }

  return (
    <div className="cd-actions-menu-wrap" ref={wrapRef}>
      <Button value={`Åtgärder (${items.length}) ▾`} className="main-toolbar-item button" onClick={() => setOpen(o => !o)} />
      {open &&
        <div className="cd-actions-menu">
          <button type="button" onClick={() => run(onChangeStage)}>Ändra skede</button>
          <button type="button" onClick={() => run(onReassignOwner)}>Byt ägare</button>
          <button type="button" onClick={() => run(onUpdateSource)}>Uppdatera källa</button>
          <button type="button" onClick={() => run(onExport)}>Exportera (CSV)</button>
          <button type="button" className="cd-actions-menu-danger" onClick={() => run(onDelete)}>Ta bort</button>
        </div>
      }
    </div>
  )
}

export const CandidateList = () => {
  const navigate = useNavigate()
  const [candidates, setCandidates] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [loading, setLoading] = useState(true)
  // Clicking a stage card filters the table to that stage; clicking the active one clears.
  // One stage at a time — the cards read as "show me these", and a multi-select set of
  // cards would need chips or some other way to show what's on, which the pills already are.
  const [stageFilter, setStageFilter] = useState(null)

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const {
    showBulkAddCandidateDialog,
    showEditCandidateDialog,
    showAddMilestoneDialog,
    showDeleteCandidateDialog,
    showReassignOwnerDialog,
    showUpdateSourceDialog,
  } = useCandidateDialog()

  useEffect(() => {
    fetchItems()
  }, [])

  // Narrowing to a stage must drop any selected row it hides, or a bulk action would fire on
  // candidates you can no longer see. Same guard events' GuestList applies for its facets.
  // Keyed on stageFilter, not on the filtered array (whose identity changes every render).
  useEffect(() => {
    if (!stageFilter) return
    setSelectedItems(prev => prev.filter(i => i.current_milestone_type === stageFilter))
  }, [stageFilter])

  const fetchItems = async () => {
    await candidateService.getAll()
      .then(res => setCandidates((res.candidates || []).map(c => ({
        ...c,
        // The shared DataTable sorts by raw field value, which would sort
        // current_milestone_type alphabetically — this mirrors it as the
        // stage's position in the pipeline instead, sorted on but never shown.
        current_milestone_sort_order: MILESTONE_TYPES.findIndex(m => m.value === c.current_milestone_type),
        // Rendered as plain text rather than a `type: 'datetime'` column, whose shared
        // formatter appends seconds ("09:00:00") — noise on a booked slot. The slot arrives
        // as wall-clock "YYYY-MM-DDTHH:MM:SS", so dropping the T and the seconds is a slice,
        // and the resulting string still sorts chronologically.
        next_interview_display: c.next_interview_start_timestamp
          ? `${c.next_interview_start_timestamp.slice(0, 10)} ${c.next_interview_start_timestamp.slice(11, 16)}`
          : '',
      }))))
      .catch(err => showMessage(JSON.stringify(err), 'error'))
      .finally(() => setLoading(false))
  }

  const addCandidatesBulk = async (payload) => {
    await candidateService.createBulk(payload)
      .then(res => { hideModalDialog(); fetchItems(); showMessage(`${res.candidates.length} kandidater skapade`+((res.skipped&&res.skipped.length)?`, ${res.skipped.length} hoppades över (saknar e-post/LinkedIn eller redan kandidat)`:``), (res.skipped&&res.skipped.length)?'warning':undefined) })
      .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
  }

  // Only reachable via the per-row pencil icon (hidden from the bulk toolbar
  // below, since editing full details only ever makes sense for one candidate
  // at a time), so `items` is always a single-item array here.
  const editCandidate = (items) => {
    showEditCandidateDialog(items, async (payload) => {
      await candidateService.update(items[0].candidate_uuid, payload)
        .then(() => { hideModalDialog(); fetchItems(); showMessage('Kandidat uppdaterad') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  const changeStage = (items) => {
    showAddMilestoneDialog(items, async (payload) => {
      await Promise.all(items.map(c => candidateService.addMilestone(c.candidate_uuid, payload)))
        .then(() => { hideModalDialog(); fetchItems(); showMessage(items.length > 1 ? `Skede uppdaterat för ${items.length} kandidater` : 'Skede uppdaterat') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  const reassignOwner = async (items) => {
    const { users } = await candidateService.getUsers()
      .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })

    showReassignOwnerDialog(items, users || [], async (payload) => {
      await Promise.all(items.map(c => candidateService.update(c.candidate_uuid, payload)))
        .then(() => { hideModalDialog(); fetchItems(); showMessage(items.length > 1 ? `Ägare uppdaterad för ${items.length} kandidater` : 'Ägare uppdaterad') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  const updateSource = (items) => {
    showUpdateSourceDialog(items, async (payload) => {
      await Promise.all(items.map(c => candidateService.update(c.candidate_uuid, payload)))
        .then(() => { hideModalDialog(); fetchItems(); showMessage(items.length > 1 ? `Källa uppdaterad för ${items.length} kandidater` : 'Källa uppdaterad') })
        .catch(err => { showMessage(JSON.stringify(err), 'error'); throw err })
    })
  }

  // Client-side only — no export endpoint needed, the list already has
  // everything a CSV needs. The leading BOM keeps Excel from mangling å/ä/ö
  // when it opens the file (a plain UTF-8 CSV without it reads as Latin-1).
  const exportSelected = (items) => {
    const headers = ['Namn', 'Ägare', 'Email', 'Telefon', 'LinkedIn', 'Källa', 'Skede', 'Nästa intervju', 'Senaste aktivitet']
    const escape = (value) => {
      const s = value == null ? '' : String(value)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = items.map(c => [
      c.candidate_name,
      c.owner_name,
      c.candidate_email,
      c.candidate_phone,
      c.candidate_linkedin,
      c.candidate_source,
      MILESTONE_PILL_VALUES[c.current_milestone_type]?.label || c.current_milestone_type,
      c.next_interview_start_timestamp
        ? `Intervju ${c.next_interview_round}: ${new Date(c.next_interview_start_timestamp).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}`
        : '',
      c.current_milestone_date ? new Date(c.current_milestone_date).toLocaleDateString('sv-SE') : '',
    ].map(escape).join(','))

    const csv = '﻿' + [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kandidater_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const deleteCandidates = (items) => {
    showDeleteCandidateDialog(items, async (data) => {
      await Promise.all(data.map(c => candidateService.remove(c.candidate_uuid)))
        .then(() => { hideModalDialog(); setSelectedItems([]); fetchItems(); showMessage('Borttaget') })
        .catch(err => showMessage(JSON.stringify(err), 'error'))
    })
  }

  // The stage snapshot: one card per active stage, count + pill. This view IS the app's
  // landing page, so the snapshot lives here — and since the table is right underneath, a
  // card filters it rather than navigating off to the board.
  const activeStages = MILESTONE_TYPES.filter(m => !CLOSED_MILESTONES.has(m.value))
  // Counts come from the FULL set, never the filtered one — otherwise picking a stage would
  // zero out every other card and you'd lose the overview the row exists to give.
  const countFor = (value) => candidates.filter(c => c.current_milestone_type === value).length

  // Filtering happens before the data reaches <DataTable> (the repo-wide convention — see
  // network's PersonList), so its own sort/paging/selection only ever see visible rows.
  const visibleCandidates = stageFilter
    ? candidates.filter(c => c.current_milestone_type === stageFilter)
    : candidates

  return (
    <div className="Candidates wide-margin">

      {!loading && candidates.length > 0 && (
        <>
          <div className="cd-home-stages">
            {activeStages.map(m => (
              <button
                type="button"
                className={"cd-home-stage" + (stageFilter === m.value ? " cd-home-stage-active" : "")}
                key={m.value}
                aria-pressed={stageFilter === m.value}
                title={stageFilter === m.value ? 'Visa alla igen' : `Visa bara ${m.label}`}
                onClick={() => setStageFilter(prev => (prev === m.value ? null : m.value))}
              >
                <span className={m.pillClassName}>{m.label}</span>
                <span className="cd-home-stage-count">{countFor(m.value)}</span>
              </button>
            ))}
          </div>
          {stageFilter && (
            <p className="cd-filter-count">
              Visar {visibleCandidates.length} av {candidates.length} kandidater ·{' '}
              <button type="button" className="cd-stage-filter-clear" onClick={() => setStageFilter(null)}>Visa alla</button>
            </p>
          )}
        </>
      )}

      <DataTable
        columns={[
          { id: 'candidate_uuid', name: '', type: 'button', label: 'Visa', onClick: (uuid) => navigate(`/${appName}/${uuid}`), data: { data: 'candidate_uuid' } },
          { id: 'candidate_name', name: 'Namn' },
          { id: 'owner_name', name: 'Ägare' },
          { id: 'current_milestone_sort_order', name: 'Skede', type: 'pill', value: 'current_milestone_type', values: MILESTONE_PILL_VALUES },
          // The next booked interview, from v_candidate. Plain text, not 'date' (the time
          // of day is the whole point of a booking) and not 'datetime' (which adds seconds).
          { id: 'next_interview_display', name: 'Nästa intervju', className: 'prio-2' },
          { id: 'current_milestone_date', name: 'Senaste aktivitet', type: 'date', className: 'prio-2' },
        ]}
        buttons={[
          { label: "+ Ny kandidat", action: "add", mainButton: true, onClick: () => showBulkAddCandidateDialog(addCandidatesBulk) },
          { label: "Redigera", action: "edit", hideInToolbar: true, onClick: (items) => editCandidate(items || selectedItems) },
          { customButton: selectedItems.length > 0
              ? <BulkActionsMenu
                  items={selectedItems}
                  onChangeStage={changeStage}
                  onReassignOwner={reassignOwner}
                  onUpdateSource={updateSource}
                  onExport={exportSelected}
                  onDelete={deleteCandidates}
                />
              : null },
          { label: "Ta bort", action: "delete", hideInToolbar: true, onClick: (items) => deleteCandidates(items || selectedItems) },
        ]}
        data={visibleCandidates}
        idColumn='candidate_uuid'
        primaryColumn='candidate_name'
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={true}
        filterable={true}
        filterObjects={['candidate_name', 'owner_name', 'current_milestone_type']}
        filterObjectsValueMap={{ current_milestone_type: MILESTONE_PILL_VALUES }}
      />

    </div>
  )
}
